<template>
  <Loading v-if="isLoading" :type="3" />
  <section v-else>
    <b-row align-h="between">
      <h4 class="mb-4 font-weight-normal">
        {{ "sections.detailsSoftwareSection.title" | i18n }}
      </h4>
    </b-row>
    <div class="mb-4" v-for="(info, index) in infos" :key="index">
      <SoftwareDetailsBloc :info="info" />
    </div>
    <b-row>
      <GenericFormUpdate
        class="pr-2"
        :objectId="+softwareId"
        :modalId="3"
        :clientId="'' + clientId"
      />
      <GenericFormDelete
        :deleteFunction="$api.deleteSoftware"
        :objectId="+softwareId"
        :modalId="'modalSoftware-' + softwareId"
      />
    </b-row>
    <b-toast
      id="error-toast"
      toast-class="bg-danger text-white text-center"
      :no-close-button="true"
    >
      <template v-slot:default>
        <i class="exclamation circle icon mr-3"></i>
        <span>{{ "sections.detailsSoftwareSection.error" | i18n }}</span>
      </template>
    </b-toast>
  </section>
</template>
<script>
import { detailsEntries } from "@/general";
import {
  Loading,
  SoftwareDetailsBloc,
  GenericFormDelete,
  GenericFormUpdate
} from "@/components";
/**
 * @description Composante qui affiche les détails d'un logiciel
 * @author Samuel Dubé
 * @author Samuel Colassin
 * @vue-prop {String} softwareId - L'id du logiciel dont on désire les informations
 * @vue-data {Array} infos - Contient tous les blocs d'informations à afficher
 * @vue-data {Object} displayedEntries - Contient l'objet servant de masque pour afficher l'information
 * @vue-data {Number} clientId - Id du client auquel appartient le logiciel
 * @vue-data {Boolean} isLoading - Si la composante est en train de charger
 */
export default {
  components: {
    Loading,
    SoftwareDetailsBloc,
    GenericFormDelete,
    GenericFormUpdate
  },
  props: {
    softwareId: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      infos: [],
      displayedEntries: detailsEntries,
      clientId: null,
      isLoading: true
    };
  },
  /**
   * @description Effectue les requêtes afin de charger les informations au
   * moment de la création
   */
  beforeMount() {
    this.getDetails();
  },
  methods: {
    /**
     * @description Méthode qui obtient les informations du logiciel
     * @returns {Promise} Une promesse contenant les informations
     * @throws {Error} Erreur lancée quand il n'y a pas de données
     */
    async getDetails() {
      let serverId = "";
      return await this.$api
        .getSoftware(this.softwareId)
        .then(res => {
          if (res.data && res.data.data) {
            let info = this.buildInfoObject(
              "sections.detailsSoftwareSection.details",
              "sections.detailsSoftwareSection.modify",
              "edit"
            );
            serverId = res.data.data.serveur_id;
            info.entries = this.parseEntriesFromData(res.data.data);
            this.infos.push(info);
          } else {
            throw new Error("No data received");
          }
        })
        .then(async () => {
          let result = await this.$api.getServer(serverId);
          if (result.data && result.data.data) {
            this.clientId = result.data.data.client_id;
            let info = this.buildInfoObject(
              "sections.detailsSoftwareSection.serveur"
            );
            this.clientId = result.data.data.client_id;
            info.entries = this.parseEntriesFromData(result.data.data);
            this.infos.push(info);
            return result.data.data;
          } else {
            throw new Error("No data received");
          }
        })
        .catch(() => {
          this.$router.push("/404");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    /**
     * @description Méthode qui construit un objet d'information
     * @param {String} title Le titre de la section
     * @param {String} action Le texte à associer au bouton d'action
     * @param {String} icon L'icône à afficher
     * @returns {Object} L'objet d'information
     */
    buildInfoObject(title, action, icon) {
      return {
        title: title,
        action: action,
        icon: icon,
        entries: []
      };
    },
    /**
     * @description Méthode qui construit un object pour une entrée
     * @param {String} key Clé à assigner à l'entrée
     * @param {String|Number} value Texte à afficher pour l'entrée
     * @param {Function} transform Fonction à appeler pour obtenir le texte à ajouter au nom de l'entrée
     * @param {String} tooltip Texte à afficher dans le tooltip
     * @param {String} group Groupe lié à l'entrée
     * @returns {Object} L'objet d'entrée construit
     */
    buildEntryObject(key, value, transform, tooltip, group) {
      return {
        lang: this.displayedEntries[key].lang,
        key: key,
        value: this.convertValue(value, transform),
        tooltip: tooltip,
        group: group
      };
    },
    /**
     * @description Méthode qui convertit les données recueillies en se basant
     * sur la propriété displayedEntry
     * @param {Object} data Données à convertir
     * @returns {Object} Un objet contenant les entrées converties
     */
    parseEntriesFromData(data) {
      let entries = {};

      for (let [key, value] of Object.entries(data)) {
        const displayedEntry = this.displayedEntries[key];

        // S'il faut afficher l'entrée
        if (displayedEntry) {
          let entry = this.buildEntryObject(
            key,
            value,
            displayedEntry.transform,
            displayedEntry.tooltip,
            displayedEntry.group
          );

          // Si l'entrée est un groupe, on crée un array
          if (displayedEntry.group) {
            entries[entry.group] = entries[entry.group] || [];
            entries[entry.group].push(entry);
          } else {
            // Sinon on assigne l'objet
            entries[entry.key] = entry;
          }
        }
      }
      return entries;
    },
    /**
     * @description Méthode qui convertit la valeur fournie en paramêtre
     * @param {String} value Valeur à convertir
     * @param {Function} transform Transformation à appliquer à la valeur
     * @returns {String} La valeur convertie en paramètre
     */
    convertValue(value, transform) {
      if (typeof value === "boolean") {
        return this.$i18n.t(`common.${value}`);
      }
      return transform ? transform(value) : value;
    }
  }
};
</script>
